import classNames from 'classnames';
import { useState } from 'react';

import { ReplotChartNames } from '../../constants';
import { FormatType } from '../../types';
import { useChartTheme, useResizeObserver } from '../../utilities';
import { plotTestId } from '../../utilities/plotTestId';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import styles from './grouped-bar-chart.module.css';
import {
  createGroupedBarColorScale,
  type GroupedBarChartProps,
  processGroupedBarData,
} from './grouped-bar-shared';
import { HorizontalGroupedBar } from './horizontal-grouped-bar';
import { VerticalGroupedBar } from './vertical-grouped-bar';

export type {
  GroupedBarChartProps,
  GroupedBarData,
} from './grouped-bar-shared';

export const GroupedBarChart = <TData extends object>({
  className,
  data,
  loading = false,
  colorMap,
  format = FormatType.PERCENTAGE,
  colorPalette,
  tooltipContent,
  hideEmptyBars = false,
  hideLegend = false,
  showBarLabels = false,
  formatLegendLabel = (label) => label,
  maxLegendLabelWidth,
  orientation = 'horizontal',
}: GroupedBarChartProps<TData>) => {
  const { containerRef, width, height } = useResizeObserver();
  const { palette } = useChartTheme();
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);

  const isVertical = orientation === 'vertical';

  const displayData = processGroupedBarData(data, hideEmptyBars);
  const colorScale = createGroupedBarColorScale(
    displayData,
    colorMap,
    colorPalette ?? palette
  );

  const chartTestId = plotTestId(
    isVertical
      ? ReplotChartNames.GroupedBarChartVertical
      : ReplotChartNames.GroupedBarChartHorizontal
  );

  const Chart = isVertical ? VerticalGroupedBar : HorizontalGroupedBar;

  return (
    <div
      ref={containerRef}
      className={classNames(
        styles.container,
        isVertical && styles.containerVerticalFill,
        className
      )}
      data-testid={chartTestId}
    >
      <PlotLoadWrapper loading={loading} noData={displayData.length === 0}>
        <Chart
          displayData={displayData}
          colorScale={colorScale}
          width={width}
          height={height}
          hoveredSeries={hoveredSeries}
          setHoveredSeries={setHoveredSeries}
          format={format}
          showBarLabels={showBarLabels}
          hideLegend={hideLegend}
          formatLegendLabel={formatLegendLabel}
          maxLegendLabelWidth={maxLegendLabelWidth}
          tooltipContent={tooltipContent}
        />
      </PlotLoadWrapper>
    </div>
  );
};
